import { useSuspenseQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Collapsible,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Suspense, useState } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';

import { ProcessorFileCodeReadonly } from '#components/processor-file-code-readonly';
import { ProcessorFileRole } from '#graphql/generated';
import { GET_PROCESSOR_FILE } from '#graphql/queries/processor-file';

function ProcessorFileCodeBody({ fileId }: { fileId: string }) {
  const { data } = useSuspenseQuery(GET_PROCESSOR_FILE, {
    variables: { id: fileId },
  });
  const file = data?.processorFile;
  if (!file) {
    return (
      <Text color="red.500" fontSize="sm">
        No processor file found for this id.
      </Text>
    );
  }

  return <ProcessorFileCodeReadonly code={file.code} />;
}

export interface CollapsibleProcessorFileRowProps {
  fileId: string;
  sortOrder: number;
  role: ProcessorFileRole;
}

export function CollapsibleProcessorFileRow({
  fileId,
  sortOrder,
  role,
}: CollapsibleProcessorFileRowProps) {
  const [open, setOpen] = useState(false);
  const roleLabel = role === ProcessorFileRole.Main ? 'Main' : 'Prepended';

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      borderColor="border"
      overflow="hidden"
      bg="bg"
    >
      <Collapsible.Root open={open} onOpenChange={(e) => setOpen(e.open)}>
        <Collapsible.Trigger asChild>
          <Button
            type="button"
            variant="ghost"
            width="100%"
            justifyContent="flex-start"
            borderRadius="0"
            py={3}
            px={4}
            h="auto"
            _hover={{ bg: 'bg.subtle' }}
          >
            <HStack gap={3} flex={1} align="flex-start" textAlign="left">
              {open ? (
                <FiChevronDown size={18} style={{ flexShrink: 0 }} />
              ) : (
                <FiChevronRight size={18} style={{ flexShrink: 0 }} />
              )}
              <VStack align="start" gap={0} flex={1}>
                <Text fontSize="sm" fontWeight="semibold" color="fg">
                  {roleLabel}
                  <Text as="span" color="fg.muted" fontWeight="normal" ml={2}>
                    (order {sortOrder})
                  </Text>
                </Text>
                <Text
                  fontSize="xs"
                  fontFamily="mono"
                  color="fg.muted"
                  wordBreak="break-all"
                >
                  {fileId}
                </Text>
              </VStack>
            </HStack>
          </Button>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <Box px={4} pb={4} pt={0} borderTopWidth="1px" borderColor="border">
            {open ? (
              <Suspense
                fallback={
                  <HStack py={4} justify="center">
                    <Spinner size="sm" />
                    <Text fontSize="sm" color="fg.muted">
                      Loading source…
                    </Text>
                  </HStack>
                }
              >
                <ProcessorFileCodeBody fileId={fileId} />
              </Suspense>
            ) : null}
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
}
